import React, { Fragment } from 'react';
import { Box, LinearProgress, Table, TableBody, TableCell, TableContainer, TableRow, Pagination, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { get, isEmpty } from 'lodash';

import ListHeader from 'components/ListHeader';
import colors from 'themes/colors';

export const useStyles = makeStyles()((theme) => ({
  root: {
    borderCollapse: 'collapse',
  },
  tableContainer: {
    height: '100%',
    border: `1px solid #e0e0eb`,
    borderBottom: 'unset',
    maxHeight: 'calc(100vh - 320px)',
  },
  tableHeader: {
    backgroundColor: theme.palette.secondary.main,
    color: colors.white,
    whiteSpace: 'pre',
    border: `1px solid ${theme.palette.secondary.main}`,
  },
  tableCell: {
    backgroundColor: theme.palette.secondary.main,
    color: colors.white,
    textTransform: 'unset',

    '&:hover': {
      color: colors.white,
    },
  },
  tableSortLabel: {
    '&:hover': {
      color: colors.white,
    },
    '&:focus': {
      color: colors.white,
    },
    '&.Mui-active': {
      color: colors.white,
      fontWeight: 'bold',

      '& .MuiTableSortLabel-icon': {
        color: `${colors.white} !important`,
      },
    },
  },
  pagination: {
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'center',
    margin: '10px 0',
  },
  addedRow: {
    backgroundColor: '#e8e8e8',
    border: 'unset',
    '& .MuiTableCell-root': {
      fontSize: '0.8rem !important',
    },
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#e8e5ff',
    },
  },
  disabledRow: {
    opacity: 0.7,
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  loadingCell: {
    padding: '0 !important',
    border: 'none !important',
    height: 0,
    lineHeight: 0,
    overflow: 'visible',
    position: 'relative',
  },
  loadingBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: '3px !important',
    zIndex: 1,
  },
}));

function AxlTableContainer({
  isLoading,
  fields,
  data,
  result,
  filters,
  pagination,
  orderBy,
  desc,
  colSpan,
  handleRequestSort,
  tableStyles,
  onPageChange,
  expandedRows,
  expandFields,
  hover = false,
  onRowClick,
  rowClassName,
}) {
  const { classes } = useStyles();

  const totalPage = filters?.size > 0 ? Math.ceil(result?.total / filters.size) || 0 : 0;

  if (!data) {
    data = result?.items;
  }

  const headersLength = fields?.filter((f) => !f.hidden).length;

  return (
    <>
      <TableContainer style={tableStyles}>
        <Table className={classes.tableContainer} stickyHeader size="small" classes={{ root: classes.root }}>
          <ListHeader
            fields={fields}
            desc={desc}
            orderBy={orderBy}
            classTableHead={classes.tableHeader}
            classTableCell={classes.tableCell}
            classTableSortLabel={classes.tableSortLabel}
            onRequestOrder={handleRequestSort}
          />
          {/* Sticky Linear Progress on bottom of Table Header */}
          <TableBody>
            <TableRow style={{ height: 0 }}>
              <TableCell colSpan={colSpan || headersLength} className={classes.loadingCell}>
                {isLoading && <LinearProgress className={classes.loadingBar} />}
              </TableCell>
            </TableRow>
          </TableBody>
          {!data || data.length === 0 ? (
            !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell scope="row" colSpan={colSpan || headersLength} align="center">
                    No data
                  </TableCell>
                </TableRow>
              </TableBody>
            )
          ) : (
            <TableBody style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
              {data?.map((item, idx) => {
                const isExpanded = !isEmpty(expandedRows) && expandedRows?.includes(item.id);
                const isClickable = !!onRowClick;
                const extraRowClass = rowClassName ? rowClassName(item, classes) : isClickable ? classes.clickableRow : '';

                return (
                  <Fragment key={`row-${idx}`}>
                    <TableRow
                      hover={hover || isClickable}
                      className={extraRowClass}
                      onClick={
                        isClickable
                          ? (e) => {
                              // Skip row click when user clicks interactive content.
                              // Walk from target up to the row — any natively interactive
                              // element (a[href], button, input …) has tabIndex >= 0.
                              let el = e.target;
                              while (el && el !== e.currentTarget) {
                                if (el.tabIndex >= 0 || el.isContentEditable) return;
                                el = el.parentElement;
                              }
                              onRowClick(item);
                            }
                          : undefined
                      }
                    >
                      {fields.map((field, fIdx) => {
                        if (!!field?.hidden) return null;
                        return (
                          <TableCell colSpan={field?.colSpan} scope="row" align={field.align || 'left'} key={`cell-${idx}-${fIdx}`}>
                            {field.renderer ? field.renderer(get(item, field.fieldName), item, data) : get(item, field.fieldName)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {isExpanded && (
                      <>
                        <TableRow className={classes.addedRow}>
                          {expandFields?.map((item, eIdx) => (
                            <TableCell key={`${item.fieldName}-${idx}-${eIdx}`}>
                              <Typography sx={{ fontWeight: 'bold' }}>{item.label}</Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                        {item.dataExpand &&
                          item.dataExpand?.map((itemExpand, tIndex) => (
                            <TableRow key={`${itemExpand?.id}-template-${tIndex}`} className={classes.addedRow}>
                              {!isEmpty(expandFields) &&
                                expandFields?.map((field, idx) => {
                                  if (!!field?.hidden) return null;

                                  return (
                                    <TableCell colSpan={field?.colSpan} scope="row" align={field.align || 'left'} key={field.fieldName + idx}>
                                      {field.renderer ? field.renderer(get(itemExpand, field.fieldName), item, data) : get(itemExpand, field.fieldName)}
                                    </TableCell>
                                  );
                                })}
                            </TableRow>
                          ))}
                      </>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {pagination && totalPage > 1 && (
        <Box className={classes.pagination}>
          <Pagination color="primary" size="small" align="center" page={filters?.page} count={totalPage} onChange={(e, page) => onPageChange(page)} />
        </Box>
      )}
    </>
  );
}

export default AxlTableContainer;
